import {
  Avatar,
  Button,
  createListCollection,
  HStack,
  Icon,
  IconButton,
  Portal,
  Select,
  Text,
  useSelectContext,
} from "@chakra-ui/react"
import { FC } from "react"

import {
  CabinetIcon,
  CategoryIcon,
  ChevronDownIcon,
  DislikeOutlineIcon,
  EyeClosedIcon,
  EyeRegularIcon,
  FilterXIcon,
  LikeOutlineIcon,
  SortDescIcon,
  TagIcon,
} from "@/components/icons"
import { Category, Skill } from "@/types/database"
import { parseSvgWithCurrentColor } from "@/utils/svgHelpers"

interface WorkFiltersProps {
  allCategories: Category[]
  allTags: Skill[]
  hideCategories?: boolean
  selectedCategories: string[]
  selectedTags: string[]
  sortedPostsLength: number
  onClearAllFilters: () => void
  onCategoryChange: (categories: string[]) => void
  onTagsChange: (tags: string[]) => void
  onSortingChange: (sortType: string) => void
}

const SelectCategoriesTrigger = ({
  selectedCategories,
}: {
  selectedCategories: string[]
}) => {
  const select = useSelectContext()
  return (
    <Button
      aria-label="Select categories"
      {...select.getTriggerProps()}
      colorPalette={
        selectedCategories.some((cat: string) => !cat.startsWith("/"))
          ? "primary"
          : undefined
      }
      flexShrink={0}
      outlineColor={"primary.500"}
      outlineOffset={"-2px"}
      rounded={0}
      size="md"
      variant={
        selectedCategories.some((cat: string) => !cat.startsWith("/"))
          ? "solid"
          : "subtle"
      }
    >
      <Icon
        aria-hidden={true}
        color="currentColor"
        focusable={false}
        size={"sm"}
        tabIndex={-1}
      >
        <CategoryIcon />
      </Icon>
      <Text display={{ base: "none", sm: "inline-flex" }} fontSize={"sm"}>
        Categories
        {selectedCategories.some((cat: string) => !cat.startsWith("/")) &&
          ` (${selectedCategories.filter((cat: string) => !cat.startsWith("/")).length})`}
      </Text>
      <Icon aria-hidden={true} focusable={false} size={"sm"} tabIndex={-1}>
        <ChevronDownIcon />
      </Icon>
    </Button>
  )
}

const SelectTagsTrigger = ({ selectedTags }: { selectedTags: string[] }) => {
  const select = useSelectContext()
  return (
    <Button
      aria-label="Select tags"
      {...select.getTriggerProps()}
      borderRight={"1px solid {colors.border}"}
      colorPalette={
        selectedTags.some((tag: string) => !tag.startsWith("/"))
          ? "primary"
          : undefined
      }
      flexShrink={0}
      outlineColor={"primary.500"}
      outlineOffset={"-2px"}
      rounded={0}
      size="md"
      variant={
        selectedTags.some((tag: string) => !tag.startsWith("/"))
          ? "solid"
          : "subtle"
      }
    >
      <Icon
        aria-hidden={true}
        as={TagIcon}
        focusable={false}
        size={"xs"}
        tabIndex={-1}
      />
      <Text display={{ base: "none", sm: "inline-flex" }} fontSize={"xs"}>
        Tags
        {selectedTags.some((tag: string) => !tag.startsWith("/")) &&
          ` (${selectedTags.filter((tag: string) => !tag.startsWith("/")).length})`}
      </Text>
      <Icon aria-hidden={true} focusable={false} size={"xs"} tabIndex={-1}>
        <ChevronDownIcon />
      </Icon>
    </Button>
  )
}

const SelectSortTrigger = () => {
  const select = useSelectContext()
  const items: Array<{ label: string; icon: React.ReactNode } | undefined> =
    select.selectedItems
  const label = items[0]?.label
  const icon = items[0]?.icon
  return (
    <IconButton
      px="4"
      size="md"
      variant="subtle"
      {...select.getTriggerProps()}
      aria-label={label ? `Sort posts by ${label}` : "Sort posts"}
      aria-labelledby={undefined}
      borderLeft={"1px solid {colors.border}"}
      flexShrink={0}
      outlineColor={"primary.500"}
      outlineOffset={"-2px"}
      rounded={0}
    >
      {select.hasSelectedItems ? (
        icon
      ) : (
        <SortDescIcon
          aria-hidden={true}
          aria-label={label}
          focusable={false}
          tabIndex={-1}
        />
      )}
    </IconButton>
  )
}

const WorkFilters: FC<WorkFiltersProps> = (props) => {
  const {
    allCategories,
    allTags,
    hideCategories = false,
    onCategoryChange,
    onClearAllFilters,
    onSortingChange,
    onTagsChange,
    selectedCategories,
    selectedTags,
    sortedPostsLength,
  } = props
  // console.log('allTags', allTags);
  const filter_sorting_type = createListCollection({
    items: [
      {
        icon: (
          <Icon
            aria-hidden={true}
            as={SortDescIcon}
            focusable={false}
            size="md"
            tabIndex={-1}
          />
        ),
        label: "Recent",
        value: "recent",
      },
      {
        icon: (
          <Icon
            aria-hidden={true}
            as={CabinetIcon}
            focusable={false}
            size="md"
            tabIndex={-1}
          />
        ),
        label: "Oldest",
        value: "oldest",
      },
      {
        icon: (
          <Icon
            aria-hidden={true}
            as={EyeRegularIcon}
            focusable={false}
            size="md"
            tabIndex={-1}
          />
        ),
        label: "Most Viewed",
        value: "most-viewed",
      },
      {
        icon: (
          <Icon
            aria-hidden={true}
            as={EyeClosedIcon}
            focusable={false}
            size="md"
            tabIndex={-1}
          />
        ),
        label: "Less Viewed",
        value: "less-viewed",
      },
      {
        icon: (
          <Icon
            aria-hidden={true}
            as={LikeOutlineIcon}
            focusable={false}
            size="md"
            tabIndex={-1}
          />
        ),
        label: "Most Liked",
        value: "liked",
      },
      {
        icon: (
          <Icon
            aria-hidden={true}
            as={DislikeOutlineIcon}
            focusable={false}
            size="md"
            tabIndex={-1}
          />
        ),
        label: "Least Liked",
        value: "least-liked",
      },
    ],
  })

  const tags_type = createListCollection({
    items: allTags.map((tag) => ({
      icon: (
        <Icon
          aria-hidden={true}
          color="fg.muted"
          focusable={false}
          size="sm"
          tabIndex={-1}
        >
          {tag.svg_logo && typeof tag.svg_logo === "string" ? (
            parseSvgWithCurrentColor(tag.svg_logo)
          ) : (
            <TagIcon focusable={false} tabIndex={-1} />
          )}
        </Icon>
      ),
      label: tag.name,
      value: tag.slug,
    })),
  })

  const categories_type = createListCollection({
    items: allCategories.map((cat) => ({
      icon: (
        <Avatar.Root shape={"rounded"} size={"xs"}>
          <Avatar.Fallback name={cat.title} />
          <Avatar.Image
            src={
              process.env.DIRECTUS_ASSETS_URL +
              cat.image.id +
              "?quality=50&width=62"
            }
          />
        </Avatar.Root>
      ),
      label: cat.title,
      value: cat.slug,
    })),
  })
  const hasSelectedCategoriesOrTags =
    selectedTags.length > 0 ||
    (!hideCategories && selectedCategories.length > 0)
  return (
    <HStack
      borderBottom={"1px solid {colors.border}"}
      flexWrap={"wrap"}
      gap={{ base: 0 }}
      justifyContent={"space-between"}
      w="full"
    >
      <HStack
        flexGrow="1"
        flexWrap="wrap"
        gap={0}
        justifyContent={{ base: "flex-start" }}
      >
        {!hideCategories && (
          <Select.Root
            borderRight={"1px solid {colors.border}"}
            collection={categories_type}
            height={"100%"}
            multiple
            onValueChange={(details) => {
              const categories = selectedCategories.filter((cat) =>
                cat.startsWith("/")
              )
              const newCat = [...categories, ...details.value]
              onCategoryChange(newCat)
            }}
            positioning={{ sameWidth: false }}
            rounded={0}
            size="md"
            value={selectedCategories.filter((cat) => !cat.startsWith("/"))}
            w={"inherit"}
          >
            <Select.HiddenSelect />
            <Select.Control>
              <SelectCategoriesTrigger
                selectedCategories={selectedCategories}
              />
            </Select.Control>
            <Portal>
              <Select.Positioner>
                <Select.Content minW="32" rounded={"xs"}>
                  {categories_type.items.map((cat) => (
                    <Select.Item cursor={"pointer"} item={cat} key={cat.value}>
                      <HStack color="fg.muted">
                        {cat.icon}
                        <Text>{cat.label}</Text>
                      </HStack>
                      <Select.ItemIndicator />
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Positioner>
            </Portal>
          </Select.Root>
        )}

        <Select.Root
          collection={tags_type}
          multiple
          onValueChange={(details) => {
            const categoryTags = selectedTags.filter((tag) =>
              tag.startsWith("/")
            )
            const newTags = [...categoryTags, ...details.value]
            onTagsChange(newTags)
          }}
          positioning={{ sameWidth: false }}
          size="sm"
          value={selectedTags.filter((tag) => !tag.startsWith("/"))}
          w={"inherit"}
        >
          <Select.HiddenSelect />
          <Select.Control>
            <SelectTagsTrigger selectedTags={selectedTags} />
          </Select.Control>
          <Portal>
            <Select.Positioner>
              <Select.Content minW="32" rounded={"xs"}>
                {tags_type.items.map((tag) => (
                  <Select.Item cursor={"pointer"} item={tag} key={tag.value}>
                    <HStack color="fg.muted">
                      {tag.icon}
                      <Text>{tag.label}</Text>
                    </HStack>
                    <Select.ItemIndicator />
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Positioner>
          </Portal>
        </Select.Root>

        {hasSelectedCategoriesOrTags && (
          <Button
            aria-label="clear all filters"
            borderRight="1px solid {colors.border}"
            color="fg.muted"
            colorPalette={"gray"}
            flexShrink={0}
            onClick={onClearAllFilters}
            rounded="0"
            size="md"
            variant="ghost"
          >
            <Icon
              aria-hidden={true}
              color="currentColor"
              focusable={false}
              size={"sm"}
              tabIndex={-1}
            >
              <FilterXIcon />
            </Icon>
            <Text display={{ base: "none", sm: "inline-flex" }} fontSize={"sm"}>
              Clear filters
            </Text>
          </Button>
        )}

        <Text
          as="span"
          color="fg.muted"
          display={{ base: "none", sm: "inline-flex" }}
          fontFamily={"mono"}
          fontSize={"sm"}
          ml="3"
        >{`${sortedPostsLength} ${sortedPostsLength === 1 ? "article" : "articles"}`}</Text>
      </HStack>
      <HStack justifyContent={{ base: "space-between" }}>
        <Select.Root
          collection={filter_sorting_type}
          defaultValue={["recent"]}
          onValueChange={(details) =>
            onSortingChange(details.value[0] || "recent")
          }
          positioning={{
            flip: false,
            placement: "bottom-end",
            sameWidth: false,
          }}
          size="sm"
          w={{ base: "auto" }}
        >
          <Select.HiddenSelect />
          <Select.Control>
            <SelectSortTrigger />
          </Select.Control>
          <Portal>
            <Select.Positioner>
              <Select.Content minW="32" rounded={"xs"}>
                {filter_sorting_type.items.map((sorting_item) => (
                  <Select.Item item={sorting_item} key={sorting_item.value}>
                    <HStack color="fg.muted">
                      {sorting_item.icon}
                      <Text>{sorting_item.label}</Text>
                    </HStack>
                    <Select.ItemIndicator />
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Positioner>
          </Portal>
        </Select.Root>
      </HStack>
    </HStack>
  )
}

export default WorkFilters
